import api from './api'
import { SKU, CreateSKUForm, SKUFilter, PaginationParams, PagedResult, ApiResponse } from '../types'

export interface SKUImportResult {
  success: boolean
  totalRows: number
  successCount: number
  skipCount: number
  errorCount: number
  errors: Array<{
    row: number
    error: string
  }>
}

export interface SKUExportParams extends SKUFilter {
  exportType?: 'filtered' | 'all' | 'byStyleNumber'
  page?: number
  pageSize?: number
}

class SKUService {
  // 获取SKU列表
  async getSKUList(filter: SKUFilter = {}, pagination: PaginationParams) {
    const params = {
      ...filter,
      page: pagination.page,
      pageSize: pagination.pageSize,
    }
    
    const response = await api.get<ApiResponse<PagedResult<SKU>>>('/skus', { params })
    return response.data.data!
  }

  // 根据ID获取SKU详情
  async getSKUById(id: string) {
    const response = await api.get<ApiResponse<SKU>>(`/skus/${id}`)
    return response.data.data!
  }

  // 根据条形码获取SKU
  async getSKUByBarcode(barcode: string) {
    const response = await api.get<ApiResponse<SKU>>(`/skus/barcode/${barcode}`)
    return response.data.data!
  }

  // 创建SKU
  async createSKU(skuData: CreateSKUForm) {
    const response = await api.post<ApiResponse<SKU>>('/skus', skuData)
    return response.data.data!
  }

  // 更新SKU
  async updateSKU(id: string, skuData: Partial<CreateSKUForm>) {
    const response = await api.put<ApiResponse<SKU>>(`/skus/${id}`, skuData)
    return response.data.data!
  }

  // 删除SKU
  async deleteSKU(id: string) {
    const response = await api.delete<ApiResponse>(`/skus/${id}`)
    return response.data
  }

  // 搜索SKU
  async searchSKUs(filter: SKUFilter) {
    const response = await api.post<ApiResponse<SKU[]>>('/skus/search', filter)
    return response.data.data!
  }

  // 获取唯一颜色代码列表
  async getUniqueColorCodes() {
    const response = await api.get<ApiResponse<string[]>>('/skus/meta/color-codes')
    return response.data.data!
  }

  // 获取唯一尺码列表
  async getUniqueSizes() {
    const response = await api.get<ApiResponse<string[]>>('/skus/meta/sizes')
    return response.data.data!
  }

  // 根据款号统计SKU数量
  async countSKUsByStyleNumber(styleNumber: string) {
    const response = await api.get<ApiResponse<{ styleNumber: string; count: number }>>(`/skus/stats/count-by-style/${styleNumber}`)
    return response.data.data!
  }

  // 导入SKU（模板1）
  async importSKUs(file: File) {
    const formData = new FormData()
    formData.append('file', file)
    
    const response = await api.post<ApiResponse<SKUImportResult>>('/skus/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data.data!
  }

  // 导入SKU（模板2）
  async importSKUs2(file: File) {
    const formData = new FormData()
    formData.append('file', file)
    
    const response = await api.post<ApiResponse<SKUImportResult>>('/skus/import2', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data.data!
  }

  // 导出SKU
  async exportSKUs(params: SKUExportParams = {}) {
    const response = await api.get('/skus/export', {
      params,
      responseType: 'blob',
    })
    
    // 创建下载链接
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })
    
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    
    // 从响应头获取文件名，如果没有则使用默认名称
    const contentDisposition = response.headers['content-disposition']
    let filename = 'SKU导出.xlsx'
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (filenameMatch && filenameMatch[1]) {
        filename = decodeURIComponent(filenameMatch[1].replace(/['"]/g, ''))
      }
    }
    
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  // 导出SKU模板1
  async exportTemplate() {
    const response = await api.get('/skus/export/template', {
      responseType: 'blob',
    })
    
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })
    
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'SKU导入模板.xlsx'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  // 导出SKU模板2
  async exportTemplate2() {
    const response = await api.get('/skus/export/template2', {
      responseType: 'blob',
    })
    
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })
    
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'SKU导入模板2.xlsx'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }
}

export default new SKUService()